:host {
  --aha-tree-item--icon-size: 20px;
  --aha-tree-item--gap: var(--spacer-1);
  --aha-tree-item--level-offset: calc((var(--aria-level, 0) - 1) * var(--aha-tree-item--icon-size));
  --aha-tree-item--line-color: var(--theme-secondary-border);
  --aha-tree-item--line-width: 1px;
  --aha-tree-item--horizontal-padding: calc(var(--spacer-3) / 2);
  --aha-tree-item--vertical-padding: var(--spacer-1);
  --aha-tree-item--background: transparent;
  --aha-tree-item--background-hover: var(--theme-secondary-background);
  --aha-tree-item--background-selected: var(--theme-tertiary-background);
  --aha-tree-item--animation-duration: ; /* Intentionally left blank. */

  /* Generate the background that will display a line extending from each collapsible button. */
  --line-start: calc(
    (var(--aha-tree-item--icon-size) / 2) + var(--aha-tree-item--horizontal-padding)
  );
  --line-end: calc(var(--line-start) + var(--aha-tree-item--line-width));
  --aha-tree-item--line-background: linear-gradient(
    to right,
    transparent var(--line-start),
    var(--aha-tree-item--line-color) var(--line-start),
    var(--aha-tree-item--line-color) var(--line-end),
    transparent var(--line-end),
    transparent var(--aha-tree-item--icon-size)
  );

  min-height: var(--aha-tree-icon--icon-size);
  display: flex;
  gap: var(--aha-tree-item--gap);
  margin-left: var(--aha-tree-item--level-offset);
  position: relative;
  border-radius: var(--Common--border-radius);
  cursor: pointer;
  padding: var(--aha-tree-item--vertical-padding) var(--aha-tree-item--horizontal-padding);
  user-select: none;
}

@media (prefers-reduced-motion) {
  :host {
    --aha-tree-item--animation-duration: 0s;
  }
}

/* Build a pseudo-element that sticks to the left of the tree item and gives the user something to hover over, even though it is empty. If the parent `aha-tree` component has the [lines] option set, then the background will be replaced with the `--aha-tree-item--line-background` above. */
:host::before {
  content: '';
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: calc(-1 * var(--aha-tree-item--level-offset));
  background: var(--aha-tree-item--background);
  background-size: var(--aha-tree-item--icon-size) 100%;
  width: calc(var(--aha-tree-item--icon-size) * (var(--aria-level) - 1));
  opacity: max(calc(1 - var(--aria-level) * 0.1), 0);
}
:host(:focus-visible) {
  z-index: 1;
  outline: var(--outline-focus-ring);
}
:host([aria-hidden='true']) {
  display: none;
}
:host(:is(:hover, :focus-within)) {
  background: var(--aha-tree-item--background-hover);
}
#group-icon {
  border-radius: var(--Common--border-radius);
  cursor: pointer;
}
#group-icon:hover {
  background: var(--aha-tree-item--background-selected);
}
:host([aria-selected='true']) {
  background: var(--aha-tree-item--background-selected);
}
:host(:not(:is([aria-expanded], [group]))) #default-icon {
  display: none;
}

#group-icon,
#icon {
  width: var(--aha-tree-item--icon-size);
  height: var(--aha-tree-item--icon-size);
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

#icon[aria-hidden='true'] {
  display: none;
}

#wrapper {
  display: flex;
  flex: 1;
  align-items: center;
  gap: inherit;
  min-height: var(--aha-tree-item--icon-size);
}

/* When the group button is not present (because the item is not a group), and if there is no icon in the "icon" slot, then add the appropriate amount of margin so that text lines up correctly. */
#wrapper:first-child:has(#icon[aria-hidden='true']) {
  margin-left: var(--aha-tree-item--gap);
}

#content {
  display: inherit;
  flex: inherit;
  gap: inherit;
  align-items: baseline;
  text-overflow: ellipsis;
  overflow: hidden;
}

#default-icon,
::slotted([slot='group-icon']) {
  line-height: 1;
  transform-origin: center;
  transform: rotate(0deg);
  transition: transform var(--aha-tree-item--animation-duration, 0.3s);
}
:host([aria-expanded='true']) #default-icon,
:host([aria-expanded='true']) ::slotted([slot='group-icon']) {
  transform: rotate(90deg);
}

::slotted([slot='']) {
  outline: none;
}
